import React from 'react';
import {Box, Chip, CircularProgress, IconButton, LinearProgress, Stack, Tooltip, Typography} from '@mui/material';
import {CheckCircle, Refresh, Warning} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {useEmbeddingHealth} from '../Hooks/useEmbeddingHealth';
import {EmbeddingHealthDto} from '../apiClient/Api';

export type EmbeddingEntity = 'device' | 'contract' | 'productExample';

interface EmbeddingHealthBadgeProps {
    /** Which entity index counters to show. Defaults to all three. */
    entities?: EmbeddingEntity[];
    /** Compact one-line variant — drops the per-entity progress bars. */
    dense?: boolean;
}

interface EntityStat {
    key: EmbeddingEntity;
    indexed: number;
    total: number;
}

const pickStats = (health: EmbeddingHealthDto, entities: EmbeddingEntity[]): EntityStat[] =>
    entities.map(key => {
        switch (key) {
            case 'device':
                return {key, indexed: health.devicesEmbedded ?? 0, total: health.deviceCandidates ?? 0};
            case 'contract':
                return {key, indexed: health.contractsEmbedded ?? 0, total: health.contractCandidates ?? 0};
            case 'productExample':
                return {key, indexed: health.productExamplesEmbedded ?? 0, total: health.productExampleCandidates ?? 0};
        }
    });

/**
 * Compact status badge for the semantic-search embedding pipeline. Shows
 * indexing progress for the selected entities and surfaces availability
 * problems (Ollama unreachable / pipeline disabled).
 */
export const EmbeddingHealthBadge: React.FC<EmbeddingHealthBadgeProps> = ({
    entities = ['device', 'contract', 'productExample'],
    dense,
}) => {
    const {t} = useTranslation();
    const {health, loading, refresh} = useEmbeddingHealth();

    if (!health && !loading) return null;

    const stats = health ? pickStats(health, entities) : [];
    const allFullyIndexed = stats.every(s => s.total === 0 || s.indexed >= s.total);
    const overallStatus: 'ready' | 'indexing' | 'unreachable' | 'disabled' =
        !health ? 'unreachable'
            : health.enabled === false ? 'disabled'
                : health.ollamaReachable === false ? 'unreachable'
                    : allFullyIndexed ? 'ready'
                        : 'indexing';

    const statusChip = (() => {
        switch (overallStatus) {
            case 'ready':
                return (
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircle/>}
                        label={t('common.embeddingHealth.ready')}
                    />
                );
            case 'indexing':
                return (
                    <Chip
                        size="small"
                        color="info"
                        icon={<CircularProgress size={12} sx={{color: 'inherit'}}/>}
                        label={t('common.embeddingHealth.indexing')}
                    />
                );
            case 'unreachable':
                return (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<Warning/>}
                        label={t('common.embeddingHealth.ollamaUnreachable')}
                    />
                );
            case 'disabled':
                return (
                    <Chip
                        size="small"
                        color="default"
                        variant="outlined"
                        icon={<Warning/>}
                        label={t('common.embeddingHealth.disabled')}
                    />
                );
        }
    })();

    const tooltipTitle = health
        ? `${t('common.embeddingHealth.model')}: ${health.model ?? '—'}${health.ollamaUrl ? ` · ${health.ollamaUrl}` : ''}`
        : '';

    return (
        <Box sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
        }}>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                <Tooltip title={tooltipTitle}>
                    <span>{statusChip}</span>
                </Tooltip>

                {!dense && stats.map(stat => {
                    if (stat.total === 0) return null;
                    const percent = Math.min(100, Math.round((stat.indexed / stat.total) * 100));
                    const fullyIndexed = stat.indexed >= stat.total;
                    return (
                        <Box key={stat.key} sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 180}}>
                            <Typography variant="caption" color="text.secondary" sx={{minWidth: 90}}>
                                {t(`common.embeddingHealth.entity.${stat.key}`)}
                            </Typography>
                            <Box sx={{flex: 1, minWidth: 60}}>
                                <LinearProgress
                                    variant="determinate"
                                    value={percent}
                                    color={fullyIndexed ? 'success' : 'info'}
                                    sx={{height: 6, borderRadius: 3}}
                                />
                            </Box>
                            <Typography variant="caption" sx={{fontVariantNumeric: 'tabular-nums', minWidth: 70, textAlign: 'right'}}>
                                {fullyIndexed
                                    ? t('common.embeddingHealth.fullyIndexed', {total: stat.total})
                                    : t('common.embeddingHealth.indexedOf', {indexed: stat.indexed, total: stat.total})}
                            </Typography>
                        </Box>
                    );
                })}

                {dense && stats.map(stat => {
                    if (stat.total === 0) return null;
                    const fullyIndexed = stat.indexed >= stat.total;
                    return (
                        <Chip
                            key={stat.key}
                            size="small"
                            variant="outlined"
                            color={fullyIndexed ? 'success' : 'info'}
                            label={`${t(`common.embeddingHealth.entity.${stat.key}`)}: ${stat.indexed}/${stat.total}`}
                            sx={{fontVariantNumeric: 'tabular-nums'}}
                        />
                    );
                })}

                <Box sx={{flex: 1}}/>

                <Tooltip title={t('common.embeddingHealth.refresh')}>
                    <span>
                        <IconButton size="small" onClick={refresh} disabled={loading}>
                            {loading ? <CircularProgress size={14}/> : <Refresh fontSize="small"/>}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Box>
    );
};
